import type { MetaOAuthRedirect } from '@/types/integrations';

/**
 * Lectura del resultado que el onboarding OAuth de Meta deja en la URL del panel.
 *
 * Flujo real (ver `docs/ONBOARDING_OAUTH.md`): si el backend tiene configurado
 * `META_OAUTH_FRONTEND_REDIRECT`, el callback público redirige el navegador al panel con el
 * resultado en la query string:
 *
 *   ?oauth=connected&channel=whatsapp|messenger|instagram&pageId=…&pageName=…
 *   ?oauth=select&channel=…&ticket=…
 *   ?oauth=error&message=…
 *
 * Es una función pura (recibe los `URLSearchParams`) para poder verificarla sin navegador:
 * el smoke comprueba los tres casos y que los parámetros se puedan limpiar de la URL.
 */

/** Parámetros que Meta deja en la URL (se borran cuando el usuario cierra el aviso). */
export const META_OAUTH_PARAM_KEYS = ['oauth', 'channel', 'pageId', 'pageName', 'ticket', 'message'] as const;

function parseChannel(value: string | null): MetaOAuthRedirect['channel'] {
  return value === 'instagram' || value === 'messenger' ? value : null;
}

function parseStatus(value: string | null): MetaOAuthRedirect['status'] {
  return value === 'connected' || value === 'select' || value === 'error' ? value : null;
}

/** Estado del onboarding a partir de la query string del panel. */
export function metaOAuthRedirectFrom(params: URLSearchParams): MetaOAuthRedirect {
  return {
    status: parseStatus(params.get('oauth')),
    channel: parseChannel(params.get('channel')),
    pageName: params.get('pageName'),
    ticket: params.get('ticket'),
    message: params.get('message'),
  };
}

/** URLSearchParams sin los parámetros del onboarding (para limpiar la URL del panel). */
export function withoutMetaOAuthParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of META_OAUTH_PARAM_KEYS) next.delete(key);
  return next;
}
